import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Columns3, AlertTriangle, Repeat2 } from "lucide-react";
import { getFullName } from "@/lib/userUtils";
import PostInteractions from "./PostInteractions";
import MarkdownBody from "./MarkdownBody";
import { feedPostAnchorId, boardHrefForCard } from "@/lib/permalinks";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import SocialUserAvatar from "@/components/avatars/SocialUserAvatar";
import OrganizationAvatar from "@/components/avatars/OrganizationAvatar";
import { cn } from "@/lib/utils";
import { markdownToPlain } from "@/lib/markdownPlain";
import {
  formatCriticalRemaining,
  isPostCriticallyActive,
} from "@/lib/feedPosts";
import {
  formatAbsoluteDate,
  formatPostTimestamp,
} from "@/lib/formatRelativeTime";
import PostAttachmentChips from "@/components/PostAttachmentChips";
import MediaCarousel from "@/components/MediaCarousel";
import { resolvePostAttachmentSrc } from "@/lib/postAttachments";
import { resolveMediaUrl, resolveSocialMediaSrc } from "@/lib/socialMedia";
import { useSocialPaths } from "@/lib/socialPaths";
import VerifiedOrgMark from "@/components/VerifiedOrgMark";
import { workKindLabel } from "@/lib/workKind";
import { cardAttributionParts } from "@/lib/cardAttribution";

const PostCard = ({
  post,
  onExpand,
  likeCount,
  commentCount,
  repostCount,
  accessToken,
  onLike,
  onComment,
  isLiked = false,
  viewerHasReposted = false,
  highlighted = false,
  /** Community Space: author row above title, more prominent */
  authorAboveTitle = false,
  /** Social home feed: Reddit-style c/community then author subtext then title */
  socialHome = false,
  /** Relative ages (default true for timeline density). */
  relativeTimestamps = true,
  /**
   * Community / social tombstones: "This post was deleted".
   * Workplace keeps "Deleted by …". Future: "Removed by moderator".
   */
  socialTombstone = false,
  /** Compact nested card inside quote/bare (no outer chrome duplication). */
  embed = false,
  socialRepost = false,
  onRepostChange = null,
  onQuoteCreated = null,
  onSavedChange = null,
  /** Like/comment handlers for nested root embed (quote cards). */
  onEmbedLike = null,
  onEmbedComment = null,
  /** Open the embedded root thread (quote cards). */
  onEmbedExpand = null,
  embedIsLiked = false,
  teamMemberIds = null,
}) => {
  const navigate = useNavigate();
  const paths = useSocialPaths();
  const postId = post.post_id || post.postId;
  const anchorId = feedPostAnchorId(postId);
  const repostKindEarly = post.repost_kind || post.repostKind || null;
  // Quote commentary is body-only — never show a title on the outer quote card.
  const title =
    repostKindEarly === "quote"
      ? null
      : markdownToPlain(post.title) || post.title || null;
  const content = post.content;
  const user = post.user;
  const team = post.team;
  const org = post.org;
  const tags = post.tags;
  const createdAt = post.created_at || post.createdAt;
  const editedAt = post.edited_at || post.editedAt;
  const deletedAt = post.deleted_at || post.deletedAt;
  const deletedBy = post.deletedBy || post.deleted_by;
  const kanbanStatus = post.kanbanStatus || post.kanban_status;
  const assignees = Array.isArray(post.assignees)
    ? post.assignees
    : post.assignee
      ? [post.assignee]
      : [];
  const assigneeName =
    assignees.length === 0
      ? null
      : assignees.length === 1
        ? getFullName(assignees[0])
        : assignees.length === 2
          ? `${getFullName(assignees[0])}, ${getFullName(assignees[1])}`
          : `${getFullName(assignees[0])}, ${getFullName(assignees[1])} +${
              assignees.length - 2
            }`;
  const teamName = team?.name;
  const isKanbanCard = Boolean(kanbanStatus);
  const cardAttribution = isKanbanCard
    ? cardAttributionParts(post, { teamMemberIds })
    : null;
  const isDeleted = Boolean(deletedAt);
  const authorOrg = post.author_org || post.authorOrg || null;
  const socialAuthor = post.social_author || post.socialAuthor || null;
  const community = post.community || null;
  const communityId =
    community?.community_id ?? post.community_id ?? post.communityId ?? null;
  const communityName = community?.display_name || null;
  const isCommunityPost = Boolean(communityId && communityName);
  const orgAuthorName = authorOrg?.name || authorOrg?.display_name || null;
  const authorDisplayName =
    orgAuthorName ||
    socialAuthor?.display_name ||
    user?.display_name ||
    getFullName(user) ||
    "Unknown";
  const authorHandle = socialAuthor?.username || user?.username || null;
  const orgSocialHandle = authorOrg?.username || null;
  const profileHref = orgSocialHandle
    ? paths.org(orgSocialHandle)
    : authorHandle
      ? paths.user(authorHandle)
      : null;
  const authorAvatarUrl =
    authorOrg?.avatar_url ||
    authorOrg?.avatarUrl ||
    socialAuthor?.avatar_url ||
    socialAuthor?.avatarUrl ||
    user?.avatar_url ||
    user?.avatarUrl ||
    null;
  const useOrgAvatar = Boolean(authorOrg);
  // Do not key social avatars off relativeTimestamps — workplace feeds use
  // relative time but hydrate member_avatar_url for OrgMemberAvatar.
  const useSocialAvatar = Boolean(
    !useOrgAvatar && (socialAuthor || socialHome || socialTombstone)
  );
  const deletedByName = useOrgAvatar
    ? orgAuthorName || "organization"
    : getFullName(deletedBy) || "Unknown";
  const handleLabel = orgSocialHandle || authorHandle;
  const isCritical = isPostCriticallyActive(post);
  const criticalUntil = post.critical_until || post.criticalUntil;
  const criticalRemaining = isCritical
    ? formatCriticalRemaining(criticalUntil)
    : null;
  const attachments = Array.isArray(post.attachments) ? post.attachments : [];
  const isSocialMediaPost = Array.isArray(post.media);
  const mediaAttachments = attachments.filter((a) => a.is_image || a.is_video);
  const chipAttachments = attachments.filter((a) => !a.is_image && !a.is_video);
  const mediaItems = isSocialMediaPost
    ? post.media
        .filter(Boolean)
        .map((m) =>
          m.type === "video_embed" ? m : { ...m, url: resolveMediaUrl(m.url) }
        )
    : [
        ...mediaAttachments.map((a) => ({
          type: a.is_video ? "video" : "image",
          attachmentId: a.attachment_id ?? a.attachmentId,
          alt: a.file_name || a.fileName,
        })),
        ...(post.embed ? [post.embed] : []),
      ];
  const repostKind = repostKindEarly;
  const repostOf = post.repost_of || post.repostOf || null;
  const resolvedLikeCount =
    likeCount ?? post.like_count ?? post.likeCount ?? 0;
  const resolvedCommentCount =
    commentCount ?? post.reply_count ?? post.replyCount ?? 0;
  const resolvedRepostCount =
    repostCount ?? post.repost_count ?? post.repostCount ?? 0;
  const resolvedViewerReposted =
    viewerHasReposted || Boolean(post.viewer_has_reposted);

  const socialInteractionProps = {
    accessToken,
    socialRepost: socialRepost || socialHome || socialTombstone,
    onRepostChange,
    onQuoteCreated,
    onSavedChange,
  };

  // Bare repost: attribution + amplified card (original or full quote)
  if (!embed && repostKind === "bare" && repostOf) {
    const nestedIsQuote =
      (repostOf.repost_kind || repostOf.repostKind) === "quote";
    return (
      <div
        id={anchorId || undefined}
        className={cn(
          "space-y-2 border-b border-border/60 py-4",
          highlighted && "feed-post-appear"
        )}
      >
        <p className="flex items-center gap-1.5 px-1 text-xs font-medium text-muted-foreground">
          <Repeat2 className="h-3.5 w-3.5" />
          <span className="inline-flex items-center gap-1 min-w-0">
            {profileHref ? (
              <Link
                to={profileHref}
                className="hover:underline truncate"
                onClick={(e) => e.stopPropagation()}
              >
                {authorDisplayName}
              </Link>
            ) : (
              <span className="truncate">{authorDisplayName}</span>
            )}
            {useOrgAvatar ? <VerifiedOrgMark org={authorOrg} /> : null}
          </span>{" "}
          reposted
        </p>
        <PostCard
          post={repostOf}
          onExpand={onExpand}
          accessToken={accessToken}
          onLike={onLike}
          onComment={onComment}
          isLiked={isLiked}
          authorAboveTitle={authorAboveTitle}
          socialHome={socialHome}
          relativeTimestamps={relativeTimestamps}
          socialTombstone={socialTombstone}
          socialRepost={socialInteractionProps.socialRepost}
          onRepostChange={onRepostChange}
          onQuoteCreated={onQuoteCreated}
          onEmbedLike={nestedIsQuote ? onEmbedLike : undefined}
          onEmbedComment={nestedIsQuote ? onEmbedComment : undefined}
          onEmbedExpand={nestedIsQuote ? onEmbedExpand : undefined}
          embedIsLiked={nestedIsQuote ? embedIsLiked : false}
          embed
        />
      </div>
    );
  }

  const boardTeamId =
    post.teamId || post.team_id || post.team?.team_id || post.team?.teamId;
  const boardOrgId =
    post.orgId ||
    post.org_id ||
    post.org?.org_id ||
    post.org?.orgId ||
    post.team?.org_id ||
    post.team?.orgId;
  const canViewOnBoard = isKanbanCard && boardTeamId && boardOrgId && !isDeleted;

  const resolveOrgAttachmentSrc = (item) =>
    resolvePostAttachmentSrc({
      postId,
      attachmentId: item.attachmentId,
      orgId: boardOrgId,
      accessToken,
    });
  const resolveSocialSrc = accessToken
    ? resolveSocialMediaSrc(accessToken)
    : undefined;

  const contentPreview =
    content && content.length > 200
      ? `${content.substring(0, 200)}...`
      : content;

  const handleCardClick = (e) => {
    if (e.target.closest("button") || e.target.closest("a")) return;
    if (onExpand) {
      onExpand();
    }
  };

  const handleViewOnBoard = (e) => {
    e.stopPropagation();
    const href = boardHrefForCard({
      orgId: boardOrgId,
      teamId: boardTeamId,
      postId,
      sprintId: post?.sprint_id ?? post?.sprintId ?? null,
    });
    if (href) navigate(href);
  };

  const whenLabel = formatPostTimestamp(isDeleted ? deletedAt : createdAt, {
    relative: relativeTimestamps,
    compact: relativeTimestamps,
  });
  const whenAbsolute = formatAbsoluteDate(isDeleted ? deletedAt : createdAt);

  const avatarNode = useOrgAvatar ? (
    <OrganizationAvatar
      org={authorOrg}
      className="h-10 w-10 shrink-0"
      fallbackClassName="text-xs"
    />
  ) : useSocialAvatar ? (
    <SocialUserAvatar
      user={user}
      avatarUrl={authorAvatarUrl}
      displayName={authorDisplayName}
      className="h-10 w-10 shrink-0"
      fallbackClassName="text-xs"
      to={profileHref}
    />
  ) : (
    <OrgMemberAvatar
      user={user}
      className="h-10 w-10 shrink-0"
      fallbackClassName="text-xs"
    />
  );

  const nameNode = profileHref ? (
    <Link
      to={profileHref}
      className="min-w-0 truncate text-[15px] font-semibold text-foreground hover:underline"
      onClick={(e) => e.stopPropagation()}
      title={authorDisplayName}
    >
      {authorDisplayName}
    </Link>
  ) : (
    <span
      className="min-w-0 truncate text-[15px] font-semibold text-foreground"
      title={authorDisplayName}
    >
      {authorDisplayName}
    </span>
  );

  const shellClass = cn(
    "min-w-0 overflow-hidden",
    embed
      ? "rounded-xl border border-border/70 bg-background/80 px-3 py-3"
      : cn(
          "border-b border-border/60 px-1 py-4 sm:px-2",
          onExpand && "cursor-pointer",
          "hover:bg-muted/30 transition-colors",
          highlighted && "feed-post-appear",
          isCritical && "border-l-2 border-l-amber-500/80 pl-2 sm:pl-3"
        )
  );

  if (isDeleted) {
    return (
      <article
        id={anchorId || undefined}
        className={cn(shellClass, "opacity-90 border-dashed")}
        onClick={onExpand ? handleCardClick : undefined}
      >
        <div className="flex gap-3 min-w-0">
          <div className="shrink-0 pt-0.5">{avatarNode}</div>
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-center gap-1.5 min-w-0 text-sm">
              <span className="font-semibold text-muted-foreground italic truncate">
                {socialTombstone
                  ? post.removed_by_moderator
                    ? "Removed by a moderator"
                    : "This post was deleted"
                  : `Deleted by ${deletedByName}`}
              </span>
              {whenLabel ? (
                <>
                  <span className="text-muted-foreground/70">·</span>
                  <time
                    className="shrink-0 text-muted-foreground text-sm"
                    dateTime={deletedAt || undefined}
                    title={whenAbsolute || undefined}
                  >
                    {whenLabel}
                  </time>
                </>
              ) : null}
            </div>
            <PostInteractions
              postId={postId}
              likeCount={resolvedLikeCount}
              commentCount={resolvedCommentCount}
              repostCount={resolvedRepostCount}
              onLike={onLike}
              onComment={onExpand}
              accessToken={accessToken}
              variant="inline"
              isLiked={isLiked}
              viewerHasReposted={resolvedViewerReposted}
              post={post}
              {...socialInteractionProps}
            />
          </div>
        </div>
      </article>
    );
  }

  const metaBits = [];
  if (isKanbanCard && cardAttribution) {
    if (cardAttribution.teamName) {
      metaBits.push(
        <span key="team" className="font-medium text-foreground/80">
          {cardAttribution.teamName}
        </span>
      );
    }
    if (cardAttribution.assigned) {
      metaBits.push(
        <span key="assigned">
          Assigned to{" "}
          <span className="font-medium text-foreground/80">
            {cardAttribution.assigned}
          </span>
        </span>
      );
    }
  } else {
    if (teamName) {
      metaBits.push(
        <span key="team" className="font-medium text-foreground/80">
          {teamName}
        </span>
      );
    }
    if (assigneeName) {
      metaBits.push(
        <span key="assignee">
          Assigned to{" "}
          <span className="font-medium text-foreground/80">{assigneeName}</span>
        </span>
      );
    }
    if ((org?.name || team?.organization?.name) && !useOrgAvatar && !socialHome) {
      metaBits.push(
        <span key="org">{org?.name || team?.organization?.name}</span>
      );
    }
  }

  return (
    <article
      id={anchorId || undefined}
      className={shellClass}
      onClick={onExpand ? handleCardClick : undefined}
    >
      {socialHome && isCommunityPost ? (
        <Link
          to={paths.community(communityId)}
          className="mb-1.5 block min-w-0 max-w-full truncate pl-[3.25rem] text-xs font-semibold text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
          title={`c/${communityName}`}
        >
          c/{communityName}
        </Link>
      ) : null}

      <div className="flex gap-3 min-w-0">
        <div className="shrink-0 pt-0.5">{avatarNode}</div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-baseline gap-1.5 min-w-0 flex-wrap">
            {nameNode}
            {useOrgAvatar ? (
              <VerifiedOrgMark org={authorOrg} className="shrink-0" />
            ) : null}
            {handleLabel && (socialHome || authorAboveTitle || useSocialAvatar) ? (
              <span className="truncate text-sm text-muted-foreground">
                @{handleLabel}
              </span>
            ) : null}
            {whenLabel ? (
              <>
                <span className="text-muted-foreground/60">·</span>
                <time
                  className="shrink-0 text-sm text-muted-foreground"
                  dateTime={createdAt || undefined}
                  title={
                    [
                      whenAbsolute,
                      editedAt ? "edited" : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || undefined
                  }
                >
                  {whenLabel}
                </time>
              </>
            ) : null}
            {editedAt ? (
              <span className="text-xs italic text-muted-foreground">
                edited
              </span>
            ) : null}
          </div>

          {(isKanbanCard || isCritical) && (
            <div className="flex flex-wrap items-center gap-1.5">
              {isCritical && (
                <Badge
                  variant="destructive"
                  className="text-[11px] py-0 px-1.5 gap-1 font-medium bg-amber-600 hover:bg-amber-600"
                >
                  <AlertTriangle className="h-3 w-3" />
                  Critical
                  {criticalRemaining ? ` · ${criticalRemaining}` : ""}
                </Badge>
              )}
              {isKanbanCard && (
                <Badge
                  variant="secondary"
                  className="text-[11px] py-0 px-1.5 gap-1 font-medium"
                >
                  <Columns3 className="h-3 w-3" />
                  {workKindLabel(post)}
                </Badge>
              )}
              {isKanbanCard && kanbanStatus && (
                <Badge
                  variant="default"
                  className="text-[11px] py-0 px-1.5"
                >
                  {kanbanStatus}
                </Badge>
              )}
            </div>
          )}

          {title ? (
            <h3 className="text-[15px] font-semibold leading-snug break-words text-foreground">
              {title}
            </h3>
          ) : null}

          {contentPreview ? (
            <div className="max-h-40 overflow-hidden text-[15px] leading-relaxed">
              <MarkdownBody content={contentPreview} compact />
            </div>
          ) : null}

          {repostKind === "quote" ? (
            <div
              className="rounded-xl border border-border/70 bg-muted/10 p-1"
              onClick={(e) => e.stopPropagation()}
            >
              {repostOf && !repostOf.unavailable ? (
                <PostCard
                  post={repostOf}
                  embed
                  accessToken={accessToken}
                  onExpand={
                    onEmbedExpand ||
                    onEmbedComment ||
                    (() => {
                      /* prefer opening the root thread via onEmbedExpand from parent */
                    })
                  }
                  onLike={onEmbedLike || undefined}
                  onComment={onEmbedComment || onEmbedExpand || undefined}
                  isLiked={embedIsLiked}
                  authorAboveTitle
                  socialHome={socialHome}
                  relativeTimestamps={relativeTimestamps}
                  socialTombstone={socialTombstone}
                  socialRepost={socialInteractionProps.socialRepost}
                  onRepostChange={onRepostChange}
                  onQuoteCreated={onQuoteCreated}
                />
              ) : (
                <p className="px-2 py-3 text-sm text-muted-foreground">
                  {post.repost_of_social_post_id || post.repostOfSocialPostId
                    ? "Original unavailable"
                    : "Quoted post unavailable"}
                </p>
              )}
            </div>
          ) : null}

          {mediaItems.length > 0 ? (
            <MediaCarousel
              items={mediaItems}
              resolveSrc={
                isSocialMediaPost ? resolveSocialSrc : resolveOrgAttachmentSrc
              }
              className="rounded-xl overflow-hidden"
            />
          ) : null}

          {chipAttachments.length > 0 && boardOrgId && accessToken ? (
            <PostAttachmentChips
              attachments={chipAttachments}
              postId={postId}
              orgId={boardOrgId}
              accessToken={accessToken}
            />
          ) : null}

          {metaBits.length > 0 ? (
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
              {metaBits.map((bit, i) => (
                <React.Fragment key={bit.key || i}>
                  {i > 0 ? <span>·</span> : null}
                  {bit}
                </React.Fragment>
              ))}
            </div>
          ) : null}

          {tags && !authorAboveTitle && !socialHome ? (
            <div className="flex flex-wrap gap-1.5 items-center">
              {tags.split(",").map((tag, idx) => {
                const t = tag.trim();
                if (!t) return null;
                return (
                  <Badge
                    key={idx}
                    variant="secondary"
                    className="text-[11px] py-0 px-1.5 font-normal"
                  >
                    {t}
                  </Badge>
                );
              })}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-0.5 min-w-0">
            <PostInteractions
              postId={postId}
              likeCount={resolvedLikeCount}
              commentCount={resolvedCommentCount}
              repostCount={resolvedRepostCount}
              onLike={onLike}
              onComment={onExpand}
              accessToken={accessToken}
              variant="inline"
              isLiked={isLiked}
              viewerHasReposted={resolvedViewerReposted}
              post={post}
              {...socialInteractionProps}
            />
            {!embed && canViewOnBoard ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleViewOnBoard}
                className="h-8 gap-1 text-xs text-muted-foreground"
              >
                <Columns3 className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden sm:inline">Open on board</span>
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
};

export default PostCard;
